import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CTAButton,
  Divider,
  Eyebrow,
  Reveal,
  Section,
  SiteFooter,
} from "./primitives";
import { Faq } from "./Faq";
import { LineageCarousel } from "./LineageCarousel";
import { Testimonial } from "./Testimonial";
import {
  CALENDLY_URL,
  LINEAGE,
  TESTIMONIALS,
  THE_WORK,
} from "./constants";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#0D1B2A]/85 border-b border-[#C9A84C]/15 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
        <a
          href="#hero"
          data-testid="nav-monogram"
          className="font-serif text-2xl tracking-[0.3em] text-[#C9A84C]"
        >
          AC
        </a>
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-cta"
          className="text-[0.65rem] sm:text-xs tracking-[0.22em] uppercase text-[#F5F0E8] hover:text-[#C9A84C] transition-colors duration-300 border-b border-transparent hover:border-[#C9A84C] pb-1"
        >
          Book an Alignment Call
        </a>
      </div>
    </header>
  );
};

// SECTION 1 — HERO
const Hero = () => (
  <section
    id="hero"
    className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#0D1B2A]"
  >
    {/* full-bleed photo placeholder */}
    <div className="absolute inset-0 bg-[#0D1B2A]" aria-hidden="true">
      {/* soft ember glow rising from the lower-centre, fading upward,
          warmest at the very bottom so it bleeds into Section 2 */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_108%,rgba(184,84,30,0.30),rgba(212,105,30,0.12)_38%,transparent_72%)]" />
      {/* navy stays dominant up top — never reaches headline or nav */}
      <div className="absolute inset-x-0 top-0 h-3/5 bg-gradient-to-b from-[#0D1B2A] via-[#0D1B2A]/70 to-transparent" />
    </div>

    <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
      <Reveal>
        <h1 className="font-serif font-medium text-[#C9A84C] leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[5rem]">
          Be A Better Man.
          <br />
          Have Better Relationships.
        </h1>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 font-serif italic text-xl sm:text-2xl md:text-3xl text-[#F5F0E8] leading-snug max-w-2xl mx-auto">
          Your relationships don't lie. They show you exactly who you are, and
          exactly where the work is.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <p className="mt-10 font-sans font-light text-[#F5F0E8]/85 text-base sm:text-lg">
          Personal &amp; relational development coaching with Aaron Christos.
        </p>
      </Reveal>
      <Reveal delay={0.55}>
        <div className="mt-12">
          <CTAButton testId="hero-cta">Book an Alignment Call</CTAButton>
        </div>
      </Reveal>
      <Reveal delay={0.7}>
        <p
          data-testid="hero-cta-note"
          className="mt-6 mx-auto max-w-md font-sans font-light text-[#F5F0E8]/55 text-xs sm:text-sm leading-relaxed"
        >
          No pitch, no pressure. Just an honest conversation to get clear on
          where you're at, what's getting in the way, and whether this work is a
          fit.
        </p>
      </Reveal>
    </div>
  </section>
);

const LandingPage = () => {
  return (
    <div className="bg-[#0D1B2A] font-sans antialiased selection:bg-[#C9A84C]/30 selection:text-[#F5F0E8]">
      <Nav />
      <Hero />

      {/* SECTION 2 — THE GAP */}
      <section
        id="the-gap"
        className="relative w-full overflow-hidden bg-[#0D1B2A] text-[#F5F0E8] px-6 py-24 sm:py-32 lg:py-40"
      >
        {/* fire photograph rising from the bottom, with dark navy overlay */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 bg-cover bg-no-repeat bg-bottom"
            style={{ backgroundImage: "url('/fire-bg.png')" }}
          />
          <div className="absolute inset-0 bg-[#0D1B2A]/[0.78]" />
          <div className="absolute inset-x-0 top-0 h-2/5 bg-[radial-gradient(ellipse_90%_80%_at_50%_-15%,rgba(184,84,30,0.20),transparent_68%)]" />
        </div>

        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif font-semibold uppercase text-[#C9A84C] text-4xl sm:text-5xl md:text-6xl leading-[1.04] tracking-tight">
              There's a fire burning within you
            </h2>
          </Reveal>

          <Reveal>
            <div className="mt-20 font-sans font-normal text-[#F5F0E8]/90 text-base sm:text-lg leading-relaxed">
              <p className="max-w-xl mx-auto">
                It's a longing to love deeply and passionately; to love life, and
                to love a woman with everything you've got. It's your drive. Your
                desire. Your sexuality. Your{" "}
                <span className="text-[#C9A84C] font-bold text-xl sm:text-2xl">
                  raw primal power.
                </span>
              </p>

              <p className="my-20 font-serif font-medium tracking-wide text-3xl sm:text-4xl md:text-5xl bg-clip-text text-transparent bg-gradient-to-b from-[#E9D08A] via-[#C9A84C] to-[#9C7E33]">
                Your fire is your gift.
              </p>

              <p className="max-w-xl mx-auto">
                But without the skills to wield it, it tends to go one of two
                ways.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                Maybe you keep it small. Polite, nice, agreeable, safe. You
                swallow what you really want to say. You go quiet in the arguments
                and tell yourself it's not worth it. Your desire goes underground,
                because somewhere along the way, expressing it stopped feeling
                safe.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                Or it burns out of control. You snap. You need to be right. You
                win the argument and lose the connection. And the same fire that
                longs to love a woman deeply gets spent on a screen; the pull of
                porn is stronger than you'd like to admit, and temptation feels
                like a curse.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                For a lot of men, it's both. The fire erupts, then you hide;
                shrinking back into shame until the pressure builds and the cycle
                repeats.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                And it's the same moment every time. You feel criticised, unwanted
                or unseen... and a younger part of you takes the wheel.
              </p>

              <p className="mt-6 max-w-xl mx-auto">
                The boy who learned to keep quiet to keep the peace.
                <br />
                Or the boy who learned to fight just to be heard.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                He's doing his best to protect you. He just doesn't have what a
                man needs in a grown-up relationship.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                How could he? You were raised to be stoic, driven and in control;
                the qualities that win at work and on the field. Then at home
                you're asked to be open, attuned and emotionally available. Nobody
                showed you how to be both.
              </p>

              <p className="mt-12 max-w-xl mx-auto">
                That doesn't make you a bad man. It makes you an unskilled
                partner.
              </p>

              <p className="my-24">
                <span className="text-[#C9A84C] font-bold text-2xl sm:text-3xl">
                  And skills can be learned.
                </span>
              </p>

              <p className="max-w-xl mx-auto">
                Bring the same focus you've given your career, your training, your
                craft, and you can learn to meet the people you love without
                abandoning yourself or your boundaries.
              </p>

              <p className="my-16 max-w-2xl mx-auto font-serif italic text-2xl sm:text-3xl md:text-4xl text-[#F5F0E8] leading-snug">
                If reading this is confronting... good. It means you're ready.
              </p>

              <p className="max-w-xl mx-auto">
                <span className="text-[#C9A84C] font-bold text-xl sm:text-2xl">
                  The only way through
                </span>{" "}
                is to{" "}
                <span className="text-[#C9A84C] font-bold text-xl sm:text-2xl">
                  reclaim your fire
                </span>{" "}
                and learn to channel it; so it serves you, the people you love,
                and the world.
              </p>
            </div>
          </Reveal>
        </div>
      </section>


      {/* SECTION 3 — WHICH ONE IS YOU? */}
      <section
        id="which-one"
        className="relative w-full overflow-hidden bg-[#0D1B2A] text-[#F5F0E8] px-6 py-24 sm:py-32 lg:py-40"
      >
        {/* faint two-door background — felt, not seen */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
        >
          <div className="flex gap-10 sm:gap-24 opacity-[0.055]">
            {[0, 1].map((d) => (
              <div
                key={d}
                className="relative w-36 sm:w-52 h-[64vh] max-h-[520px] border-2 border-[#C9A84C] rounded-t-[5rem]"
              >
                <div className="absolute inset-4 sm:inset-5 border border-[#C9A84C] rounded-t-[3.5rem]" />
                <div className="absolute top-1/2 left-3 right-3 h-px bg-[#C9A84C]" />
                <div
                  className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#C9A84C] ${
                    d === 0 ? "right-6" : "left-6"
                  }`}
                />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,rgba(212,105,30,0.08),transparent_60%)]" />
        </div>

        <div className="relative max-w-3xl mx-auto">
        <Reveal className="text-center">
          <Eyebrow>Which One Is You?</Eyebrow>
          <Divider />
          <h2 className="font-serif text-[#C9A84C] text-3xl sm:text-4xl md:text-5xl leading-tight">
            If you recognise yourself here...
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-12 md:gap-10 items-stretch">
          {/* FIRST DOOR */}
          <Reveal>
            <p className="font-sans text-[0.7rem] tracking-[0.24em] uppercase text-[#C9A84C] mb-5">
              The First Door
            </p>
            <div className="space-y-5 font-sans font-light text-base sm:text-lg leading-relaxed text-[#F5F0E8]/90">
              <p>
                You're starting to question everything. Your career, your
                choices, the story you inherited about who you're supposed to be.
                Society's script doesn't fit anymore and you're done pretending
                it does.
              </p>
              <p>
                You want to know who you actually are beneath the masks. And
                you're starting to see that your closest relationships are the
                clearest mirror you've got.
              </p>
              <p className="!mt-10">
                This can be your turning point. The man who does this work stops
                living someone else's script, and starts showing up as himself;
                in his relationships, and in his life.
              </p>
            </div>
          </Reveal>

          {/* visual divider between the two doors */}
          <div
            className="hidden md:flex flex-col items-center self-stretch justify-center gap-3"
            aria-hidden="true"
          >
            <span className="block w-px flex-1 bg-gradient-to-b from-transparent via-[#C9A84C]/40 to-transparent" />
            <span className="block w-2 h-px bg-[#C9A84C]" />
            <span className="block w-2 h-px bg-[#C9A84C]" />
            <span className="block w-px flex-1 bg-gradient-to-b from-transparent via-[#C9A84C]/40 to-transparent" />
          </div>
          <Divider className="md:hidden !my-2" />

          {/* SECOND DOOR — all content stays in this column */}
          <Reveal delay={0.1}>
            <p className="font-sans text-[0.7rem] tracking-[0.24em] uppercase text-[#C9A84C] mb-5">
              The Second Door
            </p>
            <div className="space-y-5 font-sans font-light text-base sm:text-lg leading-relaxed text-[#F5F0E8]/90">
              <p>
                Your relationship is in crisis. She's threatening to leave, or
                she's already gone.
              </p>
              <p>You love her. You've promised to change, and you meant it.</p>
              <p
                data-testid="and-yet"
                className="!my-10 font-serif italic text-2xl sm:text-3xl text-transparent bg-clip-text bg-gradient-to-r from-[#C9A84C] via-[#D4691E] to-[#B8541E]"
              >
                And yet.
              </p>
              <p>
                When it heats up, your emotions take over. One minute you're
                blaming yourself. The next you're blaming her, or everyone else.
                And the same patterns play out again.
              </p>
              <p className="!mt-10">
                This can be your turning point. Whether she stays or goes, the man
                who does this work shows up differently; for her, and for every
                relationship that comes after.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Closing line — full width, centred, beneath both columns */}
        <Reveal>
          <p className="mt-16 sm:mt-20 text-center font-serif italic text-[#C9A84C] text-xl sm:text-2xl md:text-3xl leading-snug max-w-2xl mx-auto">
            Both doors lead to the same place: a life liberated by integrity,
            self-respect and authenticity.
          </p>
        </Reveal>
        <Reveal>
          <div className="mt-16 sm:mt-20">
            <Testimonial
              t={TESTIMONIALS.matteo}
              variant="dark"
              testId="testimonial-matteo"
            />
          </div>
        </Reveal>
        </div>
      </section>

      {/* SECTION 4 — THE WORK */}
      <Section variant="light" id="the-work" className="!max-w-none">
        <div className="max-w-2xl mx-auto">
          <Reveal className="text-center">
            <Eyebrow>The Work</Eyebrow>
            <Divider />
          </Reveal>
          <Reveal>
            <div className="mt-8 max-w-2xl mx-auto text-center font-sans font-light text-[#1A2535]/85 text-lg sm:text-xl leading-relaxed space-y-5">
              <p className="font-serif italic text-[#1A2535] text-2xl sm:text-3xl leading-snug">
                Every argument, every shutdown, every trigger is curriculum.
              </p>
              <p>
                The friction in your relationship is the raw material for exactly
                the man you're becoming. That's what we work with.
              </p>
            </div>
          </Reveal>
          <div className="mt-20 space-y-20 sm:space-y-24">
            {THE_WORK.map((w, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div data-testid={`work-pillar-${i}`} className="text-center">
                  <span
                    className="block font-serif text-[#C9A84C] text-3xl mb-4"
                    aria-hidden="true"
                  >
                    0{i + 1}
                  </span>
                  <h3 className="font-serif text-[#1A2535] text-2xl sm:text-3xl leading-tight mb-5">
                    {w.title}
                  </h3>
                  <p className="font-sans font-light text-[#1A2535]/80 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                    {w.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-16 sm:mt-20">
              <Testimonial
                t={TESTIMONIALS.damian}
                variant="light"
                testId="testimonial-dom"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* SECTION 5 — YOUR GUIDE */}
      <Section variant="dark" id="your-guide" className="!max-w-none">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center">
            <Eyebrow>Your Guide</Eyebrow>
            <Divider />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-[0.85fr_1.15fr] gap-12 md:gap-16 items-start mt-6">
            <Reveal>
              {/* headshot */}
              <div
                className="w-full aspect-[3/4] bg-[#0A131F] border border-[#C9A84C]/25 overflow-hidden"
                data-testid="guide-headshot"
              >
                <img
                  src="/aaron-headshot.jpg"
                  alt="Aaron Christos"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="text-[#F5F0E8]/90 font-sans font-light text-base sm:text-lg leading-relaxed">
                <p>
                  Yes, I have worldly credentials. If they matter to you, you
                  can find them{" "}
                  <a
                    href="#credentials"
                    data-testid="guide-credentials-link"
                    className="text-[#C9A84C] underline underline-offset-4 decoration-[#C9A84C]/40 hover:decoration-[#C9A84C] transition"
                  >
                    here
                  </a>
                  .
                </p>

                <p className="mt-10">
                  But the depth of what I bring to this work wasn't learned in a
                  classroom.
                </p>

                <p className="my-12 text-[#C9A84C] font-bold text-lg sm:text-xl">
                  It was lived.
                </p>

                <p className="mt-10">
                  I left school at eighteen, broke and insecure,{" "}
                  <span className="text-[#C9A84C] font-bold text-lg sm:text-xl">
                    with a fire in me I didn't yet know how to hold.
                  </span>{" "}
                  I was so fucking done with playing small. I wanted more. I
                  wanted to see, feel, taste and touch; all of it.
                </p>

                <p className="mt-10">
                  That burning desire took me across the world, and into contact
                  with something real; something beyond anything I could've
                  imagined. I began to trust myself. And life responded
                  accordingly.
                </p>

                <p className="mt-10">
                  Before the age of 24, I'd already been through three distinct
                  "hero's-journey" initiations that schooled me in ways no
                  curriculum can.
                </p>

                <p className="mt-8 italic text-[#F5F0E8]/70">
                  (If you're interested, you can read about it here:)
                </p>

                <div className="mt-4">
                  <Link
                    to="/story"
                    data-testid="read-story-btn"
                    className="inline-flex items-center justify-center border border-[#C9A84C] text-[#C9A84C] px-7 py-3 uppercase tracking-[0.18em] text-xs font-sans font-medium transition-all duration-500 hover:bg-[#C9A84C]/[0.06] hover:shadow-[0_0_24px_rgba(201,168,76,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60"
                  >
                    Read the Full Story
                  </Link>
                </div>

                <p className="mt-12">
                  Since my spiritual awakening at 21, I've channelled that same
                  fire into tens of thousands of hours devoted to understanding
                  the human condition, the psyche, the soul, and how we relate to
                  ourselves and each other inside this human "game".
                </p>

                <p className="mt-10">
                  And let me say straight up: I've also invested heavily in what
                  doesn't work. I got caught up in "the secret"; magical thinking,
                  believing all I had to do was "vibe high" to manifest my
                  desires.
                </p>

                <p className="my-12 text-[#C9A84C] font-bold text-lg sm:text-xl">
                  Life doesn't work that way.
                </p>

                <p className="mt-10">
                  But even after the awakening, and all the travels, there was
                  still something my heart yearned for. To be in deep, sacred
                  union with a woman.
                </p>

                <p className="mt-10">
                  When I found that, I thought life would be sweet. That the hole
                  in my soul would finally be filled.
                </p>

                <p className="my-12 text-[#C9A84C] font-bold text-lg sm:text-xl">
                  What I didn't foresee was that intimate relationship would take
                  me straight into every place in my psyche that still needed to
                  wake up.
                </p>

                <p className="mt-10">
                  My shadow. My unhealed wounds. The boy who'd shut down or blow
                  up when things got hard. Every rupture between us showed me
                  exactly where I still needed to grow, and I chose to do the work
                  rather than walk away from it.
                </p>

                <p className="my-16">
                  That's when I truly understood:{" "}
                  <span className="font-serif italic text-2xl sm:text-3xl text-[#C9A84C] leading-snug">
                    awakening is the beginning, not the destination.
                  </span>{" "}
                  Relationship is where it gets tested, and where it becomes real.
                </p>

                <p className="mt-10">
                  That work made me a better man. And it built the deep, loving
                  relationship I'd only ever dreamed of.
                </p>

                <p className="mt-10">
                  Today, I'm married to my soul mate. We're nearly ten years in
                  and still as passionate for one another as ever. Greer and I now
                  guide other couples to do the same through our work at{" "}
                  <a
                    href="https://www.intimacyforcouples.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#C9A84C] underline decoration-[#C9A84C]/40 underline-offset-4 hover:decoration-[#C9A84C] transition-colors"
                  >
                    Intimacy for Couples
                  </a>
                  .
                </p>

                <p className="my-12 text-[#C9A84C] font-bold text-lg sm:text-xl">
                  Everything I teach, I've lived first.
                </p>

                <p className="mt-10">
                  Yes, my foundations are set. And like you, I'm still climbing my
                  own path of growth.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* SECTION 6 — THE PROMISE */}
      <Section variant="light" id="the-promise">
        <Reveal className="text-center">
          <Eyebrow>The Promise</Eyebrow>
          <h2 className="mt-8 font-serif text-[#1A2535] text-2xl sm:text-3xl md:text-4xl leading-snug max-w-2xl mx-auto">
            When your relationship with yourself changes, every other
            relationship changes with it.
          </h2>
        </Reveal>
        <Reveal>
          <div className="mt-12">
            <Testimonial
              t={TESTIMONIALS.andrew}
              variant="light"
              testId="testimonial-andrew"
            />
          </div>
        </Reveal>
      </Section>

      {/* SECTION 7 — 1:1 COACHING */}
      <Section variant="dark" id="coaching">
        <Reveal className="text-center">
          <Eyebrow>1:1 Coaching</Eyebrow>
          <Divider />
        </Reveal>
        <Reveal>
          <div className="space-y-7 text-[#F5F0E8]/90 font-sans font-light text-base sm:text-lg leading-relaxed">
            <p>This is private, deeply personalised work.</p>
            <p>
              What makes this different: I use established developmental models —
              because we all grow through distinct, verifiable stages — to
              identify exactly where you are in your growth and what's actually
              needed to move you forward. This means the work is calibrated to
              your specific level of development, delivered in a way that makes
              sense to you, lands in your nervous system, and creates real
              change. Most coaches don't work this way. It's often why the
              insights feel good in the session and disappear a week later.
            </p>
            <p>
              We work together to understand the specific shape of your patterns
              — where they came from, how they're running your life, and what it
              would take to change them at the root. Sessions are held online,
              and the container is built around where you actually are, not
              where you think you should be.
            </p>
            <p className="font-serif italic text-xl sm:text-2xl text-[#C9A84C] text-center !mt-12 leading-snug">
              For details on pricing, structure and what's included, check out
              the facts below.
            </p>
          </div>
        </Reveal>
        <Reveal className="text-center">
          <div className="mt-14">
            <CTAButton testId="coaching-cta">
              Book an Alignment Call
            </CTAButton>
          </div>
        </Reveal>
        <Reveal>
          <div className="mt-16 sm:mt-20">
            <Testimonial
              t={TESTIMONIALS.dom}
              variant="dark"
              testId="testimonial-damian"
            />
          </div>
        </Reveal>
      </Section>

      {/* SECTION 8 — TRAINING & LINEAGE */}
      <Section variant="light" id="credentials" className="!pb-16 sm:!pb-20">
        <Reveal className="text-center">
          <Eyebrow>Training &amp; Lineage</Eyebrow>
          <Divider />
        </Reveal>
        <Reveal>
          <ul className="space-y-4 text-center">
            {LINEAGE.map((item, i) => (
              <li
                key={i}
                data-testid={`lineage-${i}`}
                className="font-serif text-xl sm:text-2xl text-[#1A2535] leading-snug"
              >
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal>
          <p className="mt-12 text-center max-w-2xl mx-auto font-sans font-light text-[#1A2535]/80 text-base sm:text-lg leading-relaxed">
            Aaron has facilitated men's retreats, couples immersions, workshops,
            private intensives and long-term mentorship containers. He
            specialises in relational accountability, emotional intelligence,
            masculine development and psychological integration.
          </p>
        </Reveal>
        <Reveal>
          <LineageCarousel />
        </Reveal>
      </Section>

      {/* SECTION 9 — BEFORE YOU BOOK */}
      <Section variant="light" id="before-you-book" className="!pt-14 sm:!pt-20">
        <Reveal className="text-center">
          <Eyebrow>FAQ</Eyebrow>
          <Divider />
        </Reveal>
        <Faq />
        <Reveal>
          <div className="mt-16 sm:mt-20">
            <Testimonial
              t={TESTIMONIALS.annabel}
              variant="light"
              testId="testimonial-annabel"
            />
          </div>
        </Reveal>
      </Section>

      {/* Adam testimonial — between FAQ and The Invitation */}
      <Section variant="dark" id="adam-testimonial">
        <Reveal>
          <Testimonial
            t={TESTIMONIALS.adam}
            variant="dark"
            testId="testimonial-adam"
          />
        </Reveal>
      </Section>

      {/* SECTION 10 — THE INVITATION */}
      <Section variant="dark" id="the-invitation">
        <Reveal className="text-center">
          <Eyebrow>The Invitation</Eyebrow>
          <Divider />
          <blockquote className="font-serif italic text-[#F5F0E8] text-2xl sm:text-3xl md:text-4xl leading-snug max-w-2xl mx-auto">
            "He who is not busy being born is busy dying."
          </blockquote>
          <p className="mt-5 font-sans text-[0.7rem] tracking-[0.24em] uppercase text-[#C9A84C]">
            — Bob Dylan
          </p>
        </Reveal>
        <Reveal>
          <div className="mt-16 space-y-7 text-[#F5F0E8]/90 font-sans font-light text-base sm:text-lg leading-relaxed">
            <p>You can either walk one of two paths in life:</p>
            <p>
              The first path being the journey towards creating a life of joy
              and meaning and living a life on your terms.
            </p>
            <p className="font-serif italic text-2xl text-[#C9A84C] text-center">
              Or
            </p>
            <p>
              You can go through the motions. Never really questioning your
              habits or your decisions. Just playing out the script that was
              handed down to you, and settling for less than what your heart
              truly desires.
            </p>
            <p>
              The thing is, and I've got to say this up-front: both paths have
              suffering, discomfort and pain.
            </p>
            <p>
              The first path, the courageous one, requires you to rise above
              challenges, being greater than your environment, breaking habits
              and cycles of addiction, and stepping out of what's comfortable
              into the unknown. It asks you to have the courage to be vulnerable,
              and to face your shadow.
            </p>
            <p>And yeah it's hard at times.</p>
            <p>But personally the alternative is even worse.</p>
            <p>
              Staying stuck in a rut, telling yourself and others that things are
              "not bad" when they're not really good either. Living in a space of
              total self-denial and delusion. Numbing yourself to the fact that
              you're actually dissatisfied with the life you're living and
              getting to the end of it all and realising the truth that was in
              your heart — never got spoken. No thanks.
            </p>
            <p>
              This work is about building and leaving a legacy: the wake you leave
              behind you. It's about how you express yourself with and around
              others, every single day. How you show up and impact others, and
              the love you leave behind.
            </p>
            <p className="text-center text-[#C9A84C] font-serif italic text-2xl sm:text-3xl !mt-12">
              That's what we're here to build.
            </p>
          </div>
        </Reveal>
        <Reveal>
          <p
            data-testid="courage-line"
            className="mt-24 sm:mt-28 text-center font-serif text-3xl sm:text-4xl md:text-5xl leading-snug text-transparent bg-clip-text bg-gradient-to-r from-[#C9A84C] via-[#D4691E] to-[#B8541E] [text-shadow:0_0_40px_rgba(184,84,30,0.15)]"
          >
            So what are you going to choose? The fear that keeps you small, or
            the courage to live a life you can be proud of.
          </p>
        </Reveal>
        <Reveal className="text-center">
          <div className="mt-16">
            <CTAButton testId="invitation-cta" ember>
              Book an Alignment Call
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
